import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Check, MessageSquareText, Moon, Palette, Sun, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/lib/auth";
import { api, errMsg } from "@/lib/api";
import { applyTheme, useTheme } from "@/lib/theme";
import { analytics } from "@/lib/analytics";
import { inDnd, usePrefs } from "@/lib/prefs";
import { notifPermission, notifSupported, requestNotifPermission, showDesktopNotification } from "@/lib/notify";
import { NOTIFICATION_SOUNDS, playNotification, previewSound } from "@/lib/sounds";
import { Slider } from "@/components/ui/slider";
import { Volume2 } from "lucide-react";
import { PushStatus } from "./PushSettings";

const TABS = [
  ["notifications", "Notifications", Bell],
  ["appearance", "Appearance", Palette],
  ["messages", "Messages & media", MessageSquareText],
];

const ThemeCard = ({ id, label, active, onPick }) => (
  <button className={`tc-theme-card${active ? " is-active" : ""}`} onClick={() => onPick(id)} data-testid={`theme-option-${id}`} aria-pressed={active}>
    <span className={`tc-theme-preview is-${id}`}>
      <span className="tc-theme-preview__rail" /><span className="tc-theme-preview__side"><i /><i className="sel" /><i /></span><span className="tc-theme-preview__main"><i /><i /><i /></span>
    </span>
    <span className="tc-theme-card__label">{id === "light" ? <Sun size={14} /> : <Moon size={14} />} {label} {active && <Check size={14} className="tc-theme-card__check" />}</span>
  </button>
);

const Appearance = () => {
  const theme = useTheme();
  const { setUser } = useAuth();
  const pick = async (t) => {
    if (t === theme) return;
    applyTheme(t);
    analytics.track("theme_changed", { category: "preferences", label: t });
    try { const { data } = await api.patch("/users/me", { theme: t }); setUser(data); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="prefs-appearance">
      <h3 className="tc-prefs__h3">Color mode</h3>
      <p className="tc-prefs__p">Choose if Total Chat's appearance should be light or dark. Your choice is saved to your profile and follows you to every device.</p>
      <div className="tc-theme-grid">
        <ThemeCard id="light" label="Light" active={theme === "light"} onPick={pick} />
        <ThemeCard id="dark" label="Dark" active={theme === "dark"} onPick={pick} />
      </div>
      <h3 className="tc-prefs__h3" style={{ marginTop: 24 }}>Theme</h3>
      <p className="tc-prefs__p">Total Quality Insurance uses the <b>Aubergine</b> theme — the same colors as your Slack workspace. {theme === "dark" ? "In dark mode the sidebar and message area switch to Slack's dark palette while keeping the aubergine accents." : ""}</p>
      <div className="tc-theme-swatches" data-testid="theme-swatches">
        {["var(--tc-topbar)", "var(--tc-rail)", "var(--tc-sidebar)", "var(--tc-sidebar-active)", "var(--tc-badge)", "var(--tc-green)"].map((c) => <span key={c} className="tc-theme-swatch" style={{ background: c }} />)}
      </div>
    </div>
  );
};

const Toggle = ({ k, label, desc, testid, prefs, update }) => (
  <label className="tc-switch-row" style={{ cursor: "pointer" }}>
    <div><h4>{label}</h4><p>{desc}</p></div>
    <Switch checked={prefs[k] !== false} onCheckedChange={(v) => update({ [k]: v })} data-testid={testid} />
  </label>
);

const usePermission = () => {
  const [perm, setPerm] = useState(notifPermission());
  useEffect(() => { const h = () => setPerm(notifPermission()); window.addEventListener("tc:notif-permission", h); return () => window.removeEventListener("tc:notif-permission", h); }, []);
  return perm;
};

const DesktopStatus = ({ prefs, update }) => {
  const perm = usePermission();
  const enable = async () => {
    const p = await requestNotifPermission();
    if (p === "granted") { toast.success("Desktop notifications are on"); if (!prefs.desktop) update({ desktop: true }); } else if (p === "denied") toast.error("Notifications are blocked in your browser settings for this site");
  };
  const test = () => {
    const n = showDesktopNotification({ title: "Total Chat", body: "This is what a notification looks like. 🎉", tag: "tc-test" });
    if (prefs.sound) playNotification();
    analytics.track("notification_test_sent", { category: "notifications", metadata: { shown: !!n } });
    if (!n) toast.info("Allow notifications in your browser to see desktop alerts");
  };
  return (
    <div className="tc-notif-status" data-testid="desktop-notif-status">
      <div className="tc-notif-status__icon">{perm === "granted" ? <BellRing size={18} /> : perm === "denied" ? <BellOff size={18} /> : <Bell size={18} />}</div>
      <div style={{ flex: 1 }}>
        <h4>Desktop notifications {perm === "granted" ? <span className="tc-pill server">Allowed</span> : perm === "denied" ? <span className="tc-pill danger">Blocked</span> : <span className="tc-pill">Not enabled</span>}</h4>
        <p>{!notifSupported() ? "This browser doesn't support desktop notifications." : perm === "granted" ? "You'll get a desktop alert while Total Chat is open in a tab, even when you're in another window." : perm === "denied" ? "Notifications are blocked for this site. Click the lock icon in your address bar to allow them." : "Allow Total Chat to show desktop alerts for the activity you choose below."}</p>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {perm !== "granted" && perm !== "denied" && notifSupported() && <button className="tc-btn tc-btn--primary sm" onClick={enable} data-testid="enable-desktop-notifications">Enable</button>}
        {perm === "granted" && <button className="tc-btn tc-btn--outline sm" onClick={test} data-testid="test-notification">Send a test</button>}
      </div>
    </div>
  );
};

const Keywords = ({ prefs, update }) => {
  const [val, setVal] = useState("");
  const add = () => {
    const words = val.split(",").map((s) => s.trim()).filter(Boolean);
    if (!words.length) return;
    update({ keywords: [...prefs.keywords, ...words.filter((w) => !prefs.keywords.some((k) => k.toLowerCase() === w.toLowerCase()))] });
    setVal("");
  };
  return (
    <div data-testid="prefs-keywords">
      <h3 className="tc-prefs__h3" style={{ marginTop: 20 }}>My keywords</h3>
      <p className="tc-prefs__p">Get notified when any of these words show up in a channel you're in, even without an @mention. Separate words with commas.</p>
      <div style={{ display: "flex", gap: 8 }}>
        <input className="tc-input sm" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="e.g. renewal, claim #, my last name" data-testid="keyword-input" />
        <button className="tc-btn tc-btn--outline sm" onClick={add} disabled={!val.trim()} data-testid="keyword-add">Add</button>
      </div>
      <div className="tc-keywords" data-testid="keyword-list">
        {prefs.keywords.map((k) => <span key={k} className="tc-chip tc-keyword" data-testid={`keyword-${k}`}>{k}<button onClick={() => update({ keywords: prefs.keywords.filter((x) => x !== k) })} title="Remove" data-testid={`keyword-remove-${k}`}><X size={12} /></button></span>)}
        {!prefs.keywords.length && <span className="muted" style={{ fontSize: 13 }}>No keywords yet.</span>}
      </div>
    </div>
  );
};

const Dnd = ({ prefs, update }) => {
  const d = prefs.dnd;
  const set = (patch) => update({ dnd: { ...d, ...patch } });
  const active = inDnd(d);
  return (
    <div data-testid="prefs-dnd">
      <h3 className="tc-prefs__h3" style={{ marginTop: 20 }}>Notification schedule</h3>
      <label className="tc-switch-row" style={{ cursor: "pointer" }}>
        <div><h4>Do not disturb {active && <span className="tc-pill danger">Active now</span>}</h4><p>Pause desktop alerts, push notifications and sounds between these hours, every day. Messages still arrive — you just won't be interrupted.</p></div>
        <Switch checked={d.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="dnd-toggle" />
      </label>
      <div className={`tc-dnd-times${d.enabled ? "" : " is-disabled"}`}>
        <label>From <input type="time" className="tc-input xs" value={d.start} disabled={!d.enabled} onChange={(e) => set({ start: e.target.value })} data-testid="dnd-start" /></label>
        <label>to <input type="time" className="tc-input xs" value={d.end} disabled={!d.enabled} onChange={(e) => set({ end: e.target.value })} data-testid="dnd-end" /></label>
      </div>
    </div>
  );
};

const Notifications = () => {
  const [prefs, update] = usePrefs();
  return (
    <div data-testid="prefs-notifications">
      <PushStatus prefs={prefs} update={update} />
      <div style={{ marginTop: 12 }}><DesktopStatus prefs={prefs} update={update} /></div>
      <h3 className="tc-prefs__h3" style={{ marginTop: 20 }}>Notify me about…</h3>
      {[["all", "All new messages", "Every message in every channel and DM you're part of."], ["mentions", "Direct messages, mentions & keywords", "Slack's default. Also includes replies in threads you're in."], ["none", "Nothing", "You'll still see badges and the unread count in the tab title."]].map(([id, label, desc]) => (
        <label key={id} className="tc-radio-row" data-testid={`notif-option-${id}`} style={{ alignItems: "flex-start" }}>
          <input type="radio" name="notif" checked={prefs.notify === id} onChange={() => { update({ notify: id }); analytics.track("notification_pref_changed", { category: "preferences", label: id }); }} style={{ marginTop: 3 }} />
          <span><span style={{ display: "block" }}>{label}</span><span className="muted" style={{ fontSize: 13 }}>{desc}</span></span>
        </label>
      ))}
      {prefs.notify === "mentions" && <Toggle k="thread_replies" label="Replies to threads I'm following" desc="Threads you started or replied in." testid="pref-thread-replies" prefs={prefs} update={update} />}
      {prefs.notify !== "none" && <Keywords prefs={prefs} update={update} />}
      <Dnd prefs={prefs} update={update} />
      <h3 className="tc-prefs__h3" style={{ marginTop: 20 }}>Sound & appearance</h3>
      <Toggle k="desktop" label="Show desktop alerts" desc="Pop up a system notification for the activity selected above." testid="pref-desktop" prefs={prefs} update={update} />
      <Sounds prefs={prefs} update={update} />
      <Toggle k="title_badge" label="Show unread count in the browser tab" desc="Shows (3) Total Chat in the tab title when you have unread mentions or DMs." testid="pref-title-badge" prefs={prefs} update={update} />
      <p className="tc-prefs__p" style={{ marginTop: 16, fontSize: 12 }}>These preferences are saved to your profile and sync instantly to every device where you're signed in.</p>
    </div>
  );
};

const Sounds = ({ prefs, update }) => {
  const s = prefs.sounds || {};
  const set = (patch) => update({ sounds: { ...s, ...patch } });
  const [vol, setVol] = useState(s.volume ?? 70);
  useEffect(() => { setVol(s.volume ?? 70); }, [s.volume]);
  return (
    <div data-testid="pref-sounds">
      <Toggle k="sound" label="Play sounds" desc="Slack-style tones for notifications, huddles and sending. Turn off for complete silence." testid="pref-sound" prefs={prefs} update={update} />
      {prefs.sound !== false && (
        <div className="tc-sounds">
          <div className="tc-sounds__row">
            <div><h4>Volume</h4><p>{vol}%</p></div>
            <div className="tc-sounds__slider"><Volume2 size={14} /><Slider value={[vol]} min={0} max={100} step={5} onValueChange={([v]) => setVol(v)} onValueCommit={([v]) => { set({ volume: v }); setTimeout(() => previewSound(s.notification || "knock"), 50); }} data-testid="pref-sound-volume" /></div>
          </div>
          <div className="tc-sounds__row">
            <div><h4>Notification sound</h4><p>Plays when a message you'd be notified about arrives.</p></div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <select className="tc-select sm" value={s.notification || "knock"} onChange={(e) => { set({ notification: e.target.value }); setTimeout(() => previewSound(e.target.value), 50); }} data-testid="pref-sound-notification">
                {NOTIFICATION_SOUNDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <button className="tc-btn tc-btn--ghost sm" onClick={() => previewSound(s.notification || "knock")} data-testid="pref-sound-preview">Preview</button>
            </div>
          </div>
          <label className="tc-switch-row" style={{ cursor: "pointer" }}>
            <div><h4>Huddle sounds</h4><p>Tones when you join or leave, when others come and go, when a huddle starts or ends, and when you mute.</p></div>
            <Switch checked={s.huddles !== false} onCheckedChange={(v) => { set({ huddles: v }); if (v) setTimeout(() => previewSound("huddle_join"), 50); }} data-testid="pref-sound-huddles" />
          </label>
          <label className="tc-switch-row" style={{ cursor: "pointer" }}>
            <div><h4>Sent-message sound</h4><p>A soft whoosh each time you send a message.</p></div>
            <Switch checked={!!s.sent} onCheckedChange={(v) => { set({ sent: v }); if (v) setTimeout(() => previewSound("message_sent"), 50); }} data-testid="pref-sound-sent" />
          </label>
        </div>
      )}
    </div>
  );
};

const Messages = () => {
  const [prefs, update] = usePrefs();
  return (
    <div data-testid="prefs-messages">
      <h3 className="tc-prefs__h3">Inline media & links</h3>
      <Toggle k="inline_previews" label="Show images and files uploaded to Total Chat" desc="Images, video and audio appear inline in the conversation. Turn off to show compact file cards instead." testid="pref-inline-previews" prefs={prefs} update={update} />
      <Toggle k="large_emoji" label="Display large emoji" desc="Emoji-only messages show at a larger size." testid="pref-large-emoji" prefs={prefs} update={update} />
    </div>
  );
};

export const PreferencesModal = ({ initialTab = "notifications", onClose }) => {
  const [tab, setTab] = useState(initialTab);
  const Body = { notifications: Notifications, appearance: Appearance, messages: Messages }[tab] || Notifications;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="tc-dialog p-0 max-w-[820px] overflow-hidden" data-testid="preferences-modal">
        <div className="tc-prefs">
          <nav className="tc-prefs__nav" data-testid="prefs-nav">
            <DialogTitle asChild><h2 style={{ padding: "0 16px 12px", fontSize: 22 }}>Preferences</h2></DialogTitle>
            <DialogDescription className="sr-only">Personal preferences for Total Chat</DialogDescription>
            {TABS.map(([id, label, Icon]) => <button key={id} className={`tc-prefs__navitem${tab === id ? " is-active" : ""}`} onClick={() => { setTab(id); analytics.track("preferences_tab_viewed", { category: "preferences", label: id }); }} data-testid={`prefs-tab-${id}`}><Icon size={16} /> {label}</button>)}
          </nav>
          <div className="tc-prefs__body tc-scroll"><Body /></div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
